import { useCallback } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { CredentialResponse, useGoogleOneTapLogin } from "@react-oauth/google";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import jwt from "jsonwebtoken";
import { useRouter } from "next/navigation";

import { useToast } from "@/components/molecules/Toast";
import { postFusionAuthGoogleOneTapLogin } from "~/api/auth-service";
import { useClientEnvironment } from "~/app/environment/client";
import {
  authErrorToasts,
  AuthErrorToastType,
} from "~/components/auth/auth-error-toasts";
import { Locale } from "~/i18n/i18n.types";
import { savePreferredLocaleCookie } from "~/i18n/i18n.utils";
import { useLocaleData } from "~/i18n/IntlProvider";
import {
  loadInitialProfile,
  redirectToUpdatedTermsIfPending,
} from "~/queries/profile/profile.service";
import { NormalizedProfile } from "~/types/profile.types";
import { signinUrlPath } from "~/utils/auth.utils";
import {
  ACCESS_TOKEN_COOKIE,
  PREFERRED_LOCALE_COOKIE,
  SSO_ID_COOKIE,
} from "~/utils/constants";

const useGoogleOneTapFusionAuth = () => {
  const router = useRouter();
  const { toast } = useToast();
  const env = useClientEnvironment();

  const { urlPrefix } = useLocaleData();

  const handleSetPerferredLocaleAfterSignin = useCallback(
    async (profile: NormalizedProfile) => {
      // safety check just in case we don't have a token in the cookies
      if (!profile) return;
      const currentPreferredLocale = getCookie(PREFERRED_LOCALE_COOKIE);
      if (profile.preferredLocale !== currentPreferredLocale) {
        // delete current preferred locale cookie if exists to avoid duplicate cookie
        deleteCookie(PREFERRED_LOCALE_COOKIE);
        savePreferredLocaleCookie(profile.preferredLocale as Locale);
      }
    },
    [],
  );

  const handleShowToastError = useCallback(
    (type: AuthErrorToastType = "generic") => {
      const signinUrl = signinUrlPath("google_one_tap_signin_error_toast");
      const options =
        type === "wrongMethod"
          ? authErrorToasts.wrongMethod(signinUrl)
          : authErrorToasts[type]();
      toast(options);
    },
    [toast],
  );

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse: CredentialResponse) => {
      try {
        // Decode the JWT to get the Google user ID (sub)
        const credential = credentialResponse?.credential;

        if (!credential) {
          throw new Error("No credential returned from Google One Tap");
        }
        const tokenData = jwt.decode(credential) as jwt.JwtPayload;

        if (!tokenData || typeof tokenData === "string") {
          throw new Error("Invalid token data returned from Google One Tap");
        }
        const { sub = "" } = tokenData as {
          email: string;
          family_name: string;
          given_name: string;
          picture: string;
          sub: string;
        };

        const response = await postFusionAuthGoogleOneTapLogin({
          googleSub: sub,
          jwtCredential: credential,
          urlPrefix: urlPrefix ?? "",
        });

        if ("error" in response) {
          const errorShownInToast =
            response.error === "profile_found_diff_signin_type"
              ? "wrongMethod"
              : undefined;
          handleShowToastError(errorShownInToast);
          return;
        }

        // user is now logged in
        // access token should be set in the cookies by the BE - so this is a safety check
        const accessTokenFromCookie = await getCookie(ACCESS_TOKEN_COOKIE);
        let accessToken = accessTokenFromCookie;

        // in local dev, FusionAuth doesn't set the cookie, so we set it here for local dev only
        if (
          !accessTokenFromCookie &&
          env.PS_ENV === "development" &&
          response?.["session-token"]
        ) {
          setCookie(ACCESS_TOKEN_COOKIE, response["session-token"]);
          accessToken = response["session-token"];
          // set cookies for user id from response
          setCookie(SSO_ID_COOKIE, response.profile.SSOid);
        }

        if (!accessToken) {
          throw new Error(
            "Google One Tap login failed: no access token was set in cookies after login",
          );
        }

        const profile = await loadInitialProfile();

        if (!profile) {
          return;
        }

        await handleSetPerferredLocaleAfterSignin(profile);
        // Send the user to updated-terms if they have a pending experience;
        // skip the normal success step when we redirect (or get logged out).
        const halted = await redirectToUpdatedTermsIfPending();
        if (!halted) {
          // If the user has no pending experience, refresh the page to update the UI
          router.refresh();
        }
      } catch (error) {
        datadogRum.addError(
          error instanceof Error
            ? error
            : new Error(
                "Google One Tap login failed, onSuccess callback error",
              ),
        );
        handleShowToastError();
      }
    },
    onError: () => {
      // google doesn't provide an error so we send one back to log it
      datadogRum.addError(
        new Error("Google One Tap login failed, onError callback triggered"),
      );
      handleShowToastError();
    },
    cancel_on_tap_outside: false,
    use_fedcm_for_prompt: true,
    auto_select: true,
    disabled: !!getCookie(ACCESS_TOKEN_COOKIE), // disable the hook (and thus the prompt) if we have an active access token cookie
  });
};

export { useGoogleOneTapFusionAuth };
